import React, { useState, useEffect } from "react";
import { Plus, KeyRound, Copy, Info, Trash2 } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { Modal } from "../components/Modal";
import { DataTable } from "../components/DataTable";
import { Badge } from "../components/Badges";
import { fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const ROLES = [
  { value: "manager", label: "Manager" }, { value: "admin", label: "Reception / Admin" },
  { value: "technician", label: "Technician" }, { value: "technical_manager", label: "Technical Manager" },
  { value: "parts", label: "Parts / Spares" }, { value: "dispatch", label: "Dispatch" },
];
const ROLE_CLS = { manager: "b-gold", admin: "b-violet", technician: "b-blue", technical_manager: "b-green", parts: "b-amber", dispatch: "b-slate" };

export default function StaffUsers({ embedded = false }) {
  const { can, user } = useAuth();
  const canEdit = can("users", "edit");
  const canDelete = ["manager", "admin"].includes(user?.role);
  const [refresh, setRefresh] = useState(0);
  const [adding, setAdding] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "technician", discipline: "" });
  const [disciplines, setDisciplines] = useState([]);

  useEffect(() => { api.get("/disciplines").then((r) => setDisciplines(r.data.map((d) => d.name))).catch(() => {}); }, []);

  const create = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    try {
      const r = await api.post("/users/staff", form);
      if (r.data.temp_password) setTempPw({ email: r.data.email, pw: r.data.temp_password });
      toast.success("Staff user created");
      setAdding(false); setForm({ name: "", email: "", role: "technician", discipline: "" });
      setRefresh((x) => x + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const resetPw = async (row) => {
    try { const r = await api.post(`/users/staff/${row.id}/reset-password`); setTempPw({ email: row.email, pw: r.data.temp_password }); toast.success("Password reset"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const removeStaff = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.delete(`/users/staff/${confirmDel.id}`);
      toast.success(`Removed ${confirmDel.name || confirmDel.email || "user"}`);
      setConfirmDel(null); setRefresh((x) => x + 1);
    } catch (e) { toast.error(apiErr(e)); } finally { setDeleting(false); }
  };

  const columns = [
    { key: "name", label: "Name", sortable: true, editable: true, render: (v) => <span className="t-strong">{v || "—"}</span> },
    { key: "email", label: "Email", sortable: true, editable: true, mono: true },
    { key: "role", label: "Role", sortable: true, editable: true, type: "select", options: ROLES, render: (v) => v ? <Badge label={ROLES.find((r) => r.value === v)?.label || v} cls={ROLE_CLS[v] || "b-slate"} /> : "—" },
    { key: "discipline", label: "Discipline", sortable: true, editable: true, type: "select", options: disciplines, render: (v) => v || "—" },
    { key: "active", label: "Active", sortable: true, editable: true, type: "select", options: [{ value: "true", label: "Active" }, { value: "false", label: "Disabled" }], render: (v) => v === false ? <Badge label="Disabled" cls="b-red" /> : <Badge label="Active" cls="b-green" /> },
    { key: "unserviceable_reviewer", label: "USR Reviewer", sortable: true, editable: true, type: "select", options: [{ value: "true", label: "Yes" }, { value: "false", label: "No" }], render: (v) => v ? <Badge label="Reviewer" cls="b-amber" testid="usr-reviewer-badge" /> : <span className="t-mut">—</span> },
    { key: "can_receive", label: "Can Receive", sortable: true, editable: true, type: "select", options: [{ value: "true", label: "Yes" }, { value: "false", label: "No" }], render: (v) => v ? <Badge label="Receiver" cls="b-green" testid="can-receive-badge" /> : <span className="t-mut">—</span> },
    { key: "also_technician", label: "Also Technician", sortable: true, editable: true, type: "select", options: [{ value: "true", label: "Yes" }, { value: "false", label: "No" }], render: (v) => v ? <Badge label="Also Technician" cls="b-blue" testid="also-technician-badge" /> : <span className="t-mut">—</span> },
    { key: "last_login_at", label: "Last login", sortable: true, render: (v) => <span className="mono t-mut">{fmtDate(v)}</span> },
    { key: "id", label: "", render: (_, row) => (canEdit || canDelete) ? (
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
        {canEdit && <button className="btn sm ghost" onClick={() => resetPw(row)} data-testid={`reset-pw-${row.id}`}><KeyRound size={13} /> Reset</button>}
        {canDelete && row.id !== user?.id && <button className="btn sm ghost" style={{ color: "var(--red)" }} onClick={() => setConfirmDel(row)} data-testid={`delete-staff-${row.id}`}><Trash2 size={13} /></button>}
      </div>
    ) : null },
  ];

  return (
    <>
      {!embedded && (
        <PageHead crumb="Administration" title="Staff Users"
          sub="Internal PTE users. Roles map to permissions configured under Settings → Permissions."
          actions={canEdit && <button className="btn gold" onClick={() => setAdding(true)} data-testid="add-staff-btn"><Plus size={15} /> Add staff user</button>} />
      )}
      {embedded && canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button className="btn gold" onClick={() => setAdding(true)} data-testid="add-staff-btn"><Plus size={15} /> Add staff user</button>
        </div>
      )}

      <DataTable endpoint="/users/staff" columns={columns} rowKey="id" testid="staff-users"
        canEdit={canEdit} editEndpoint={(row) => `/users/staff/${row.id}`}
        defaultSort={{ by: "name", dir: "asc" }} searchPlaceholder="Search name, email, role…"
        refreshSignal={refresh} />

      {adding && (
        <Modal open onClose={() => setAdding(false)} icon={Plus} title="Add staff user"
          footer={<><button className="btn ghost" onClick={() => setAdding(false)}>Cancel</button><button className="btn gold" onClick={create} data-testid="save-staff-btn">Create user</button></>}>
          <div className="form-grid">
            <div className="fld"><label>Full name <span className="req">*</span></label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></div>
            <div className="fld"><label>Email <span className="req">*</span></label><input className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" /></div>
            <div className="fld"><label>Role</label><select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role">{ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
            <div className="fld"><label>Discipline</label><select className="input" value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })}><option value="">—</option>{disciplines.map((d) => <option key={d}>{d}</option>)}</select></div>
          </div>
          <div className="notice" style={{ marginTop: 14 }}><Info size={16} /><span>A temporary password is generated. The user must set their own password on first sign-in.</span></div>
        </Modal>
      )}

      {confirmDel && (
        <Modal open onClose={() => !deleting && setConfirmDel(null)} icon={Trash2} title="Delete staff user?"
          subtitle={confirmDel.name || confirmDel.email}
          footer={<>
            <button className="btn ghost" onClick={() => setConfirmDel(null)} disabled={deleting} data-testid="delete-staff-cancel">Cancel</button>
            <button className="btn" style={{ background: "var(--red)", color: "#fff" }} onClick={removeStaff} disabled={deleting} data-testid="delete-staff-confirm">{deleting ? "Deleting…" : "Delete permanently"}</button>
          </>}>
          <p style={{ fontSize: 13, marginBottom: 8 }}>
            This permanently removes the login for <b>{confirmDel.name || confirmDel.email}</b>{confirmDel.email ? <> (<span className="mono">{confirmDel.email}</span>)</> : null}.
          </p>
          <p style={{ fontSize: 12.5, color: "var(--text-2)", margin: 0 }}>
            Jobs, certificates and calibration history they handled are <b>not</b> affected. If you only want to block sign-in, set them to <b>Disabled</b> instead. This action is logged in the audit trail.
          </p>
        </Modal>
      )}

      <TempPwModal data={tempPw} onClose={() => setTempPw(null)} />
    </>
  );
}

export function TempPwModal({ data, onClose }) {
  if (!data) return null;
  return (
    <Modal open onClose={onClose} icon={KeyRound} title="Temporary password" subtitle={data.email}
      footer={<button className="btn gold" onClick={onClose}>Done</button>}>
      <p style={{ fontSize: 13.5, color: "var(--text-2)", marginBottom: 14 }}>Share this temporary password securely. The user will be required to set a new password on first sign-in.</p>
      <div className="card pad" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--card-2)" }}>
        <span className="mono" style={{ fontSize: 16, fontWeight: 700 }} data-testid="temp-pw-value">{data.pw}</span>
        <button className="btn sm ghost" onClick={() => { navigator.clipboard?.writeText(data.pw); toast.success("Copied"); }}><Copy size={13} /> Copy</button>
      </div>
    </Modal>
  );
}
